import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, ClipboardList, ChefHat, CalendarDays, Truck, Users,
  FileBarChart2, Wallet, MessageSquare, ScanLine, Settings as SettingsIcon,
  UserCog, LogOut, Menu, Plus, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const OWNER_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, test: "nav-dashboard" },
  { to: "/orders", label: "Orders", icon: ClipboardList, test: "nav-orders" },
  { to: "/production", label: "Today's Production", icon: ChefHat, test: "nav-production" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, test: "nav-calendar" },
  { to: "/delivery", label: "Delivery / Pickup", icon: Truck, test: "nav-delivery" },
  { to: "/customers", label: "Customers", icon: Users, test: "nav-customers" },
  { to: "/expenses", label: "Expenses", icon: Wallet, test: "nav-expenses" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, test: "nav-reports" },
  { to: "/templates", label: "WhatsApp Templates", icon: MessageSquare, test: "nav-templates" },
  { to: "/extract", label: "WhatsApp Extract", icon: ScanLine, test: "nav-extract" },
  { to: "/users", label: "User Management", icon: UserCog, test: "nav-users" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, test: "nav-settings" },
];

const STAFF_NAV = [
  { to: "/production", label: "Today's Production", icon: ChefHat, test: "nav-production" },
  { to: "/orders", label: "Orders", icon: ClipboardList, test: "nav-orders" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, test: "nav-calendar" },
  { to: "/delivery", label: "Delivery / Pickup", icon: Truck, test: "nav-delivery" },
  { to: "/templates", label: "WhatsApp Templates", icon: MessageSquare, test: "nav-templates" },
];

export default function Layout() {
  const { user, logout, isOwner } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = isOwner ? OWNER_NAV : STAFF_NAV;

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-4 py-4 border-b border-[#E8E3DF] flex items-center justify-between">
        <Logo size={36} withText />
        <button className="md:hidden p-1" onClick={() => setOpen(false)} aria-label="Close menu">
          <X className="h-5 w-5" />
        </button>
      </div>
      {isOwner && (
        <div className="px-3 py-3">
          <Button
            onClick={() => { setOpen(false); nav("/orders/new"); }}
            data-testid="sidebar-new-order-btn"
            className="w-full h-9 bg-[#B86F77] hover:bg-[#A35D65] text-white font-medium"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New Order
          </Button>
        </div>
      )}
      <nav className="flex-1 overflow-y-auto px-2 pb-3 pt-3">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            onClick={() => setOpen(false)}
            data-testid={it.test}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 my-0.5 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[#F3E8E9] text-[#B86F77] font-medium"
                  : "text-[#141E30]/80 hover:bg-[#FCF9F9]"
              }`
            }
          >
            <it.icon className="h-4 w-4" />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-[#E8E3DF] p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-8 w-8 rounded-full bg-[#B86F77] text-white flex items-center justify-center text-xs font-medium">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="leading-tight">
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="text-[11px] text-[#4B5563] capitalize">{user?.role}</div>
          </div>
        </div>
        <Button
          onClick={logout}
          data-testid="logout-btn"
          variant="outline"
          className="w-full h-8 border-[#E8E3DF]"
        >
          <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Mobile topbar */}
      <div className="md:hidden flex items-center justify-between border-b border-[#E8E3DF] bg-white px-3 py-2 sticky top-0 z-30">
        <button onClick={() => setOpen(true)} aria-label="Open menu" data-testid="open-menu-btn">
          <Menu className="h-5 w-5" />
        </button>
        <Logo size={32} withText />
        <div className="w-5" />
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-[#E8E3DF] bg-white min-h-screen sticky top-0">
          {SidebarInner}
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="md:hidden fixed inset-0 z-40">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl">{SidebarInner}</aside>
          </div>
        )}

        <main className="flex-1 min-w-0 p-3 md:p-5 max-w-[1400px] mx-auto w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
